import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export interface IdleTimeoutDialogProps {
  /** Controls visibility. Driven by the parent's warning state. */
  open: boolean;
  /**
   * Total warning window in ms (e.g. 60_000). Used to seed and tick down
   * the visible countdown. The actual auto-logout is owned by the hook,
   * so a small drift between this label and the real clock is harmless.
   */
  warningMs: number;
  /**
   * Called when the user dismisses the modal (clicks Okay, hits ESC, or
   * clicks the close affordance). The parent should both hide the modal
   * and call `reset()` on the idle hook.
   */
  onAcknowledge: () => void;
}

/**
 * Session-expiry warning modal. Shows a centered dialog with a live
 * countdown to forced logout and a single "Okay" action that extends the
 * session via the idle-timeout hook's `reset()`.
 *
 * Styling intentionally relies on the shared shadcn Dialog so light/dark
 * parity, focus trap, and backdrop behaviour match every other modal in
 * the app.
 */
export function IdleTimeoutDialog({
  open,
  warningMs,
  onAcknowledge,
}: IdleTimeoutDialogProps) {
  const initialSeconds = Math.max(1, Math.ceil(warningMs / 1000));
  const [secondsLeft, setSecondsLeft] = useState<number>(initialSeconds);

  // Drive the visible countdown locally so it ticks every second without
  // re-rendering the rest of the app shell. When the modal closes we let
  // the interval die; the next open re-seeds from `warningMs`.
  useEffect(() => {
    if (!open) return;

    const seedSeconds = Math.max(1, Math.ceil(warningMs / 1000));
    setSecondsLeft(seedSeconds);
    const startedAt = Date.now();

    const id = setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      const remainingMs = Math.max(0, warningMs - elapsedMs);
      setSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
    }, 500);

    return () => clearInterval(id);
  }, [open, warningMs]);

  const handleOpenChange = (next: boolean) => {
    // Radix fires onOpenChange(false) for ESC, outside clicks, and the
    // built-in close X. We treat every dismissal as "Okay" so the user
    // can never end up in a state where the modal is gone but the timer
    // is still ticking down to logout without their knowledge.
    if (!next) onAcknowledge();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onAcknowledge();
        }}
      >
        <DialogHeader>
          <DialogTitle>Session about to expire</DialogTitle>
          <DialogDescription>
            You have been inactive for a while. You will be logged out in 1
            minute.
          </DialogDescription>
        </DialogHeader>

        <p
          className="text-sm font-medium text-foreground"
          aria-live="polite"
          role="status"
        >
          You will be logged out in {secondsLeft}{" "}
          {secondsLeft === 1 ? "second" : "seconds"}.
        </p>

        <DialogFooter>
          <Button
            autoFocus
            onClick={onAcknowledge}
            className="w-full sm:w-auto"
          >
            Okay
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
